import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import type { FilterAPI } from '~/api/Catalog';
import { company } from '~/company/Company';
import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import {
  type BreadcrumbItem,
  Breadcrumbs,
} from '~/components/isolated/Breadcrumbs';
import { MobileFilters } from '~/components/MobileFiltersV3';
import MobileSorting from '~/components/MobileSortingV3/MobileSortingV3';
import ProductFilterV3 from '~/components/ProductFilterV3/ProductFilter';
import { useGlobal } from '~/hooks/useGlobal';
import type { GetProductFilterOptions } from '~/pages/Catalog/interfaces';
import NotFound from '~/pages/NotFound/NotFound';
import { catalogStore } from '~/stores';

import { useCategoryPage } from '../../hooks';
import styles from './CatalogV3.module.scss';
import { CategoriesList } from './components/CategoriesList';
import { CategorySEOBlock } from './components/CategorySEOBlock';
import { Chips } from './components/Chips';
import { FiltersSkeleton } from './components/FiltersSkeleton';
import { ProductsView } from './components/ProductsView';
import { Sidebar } from './components/Sidebar';
import { Sorting } from './components/Sorting';
import { Toolbar } from './components/Toolbar';

const CatalogV3 = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const [searchParams] = useSearchParams();

  const showProductsParam = searchParams.get('showProducts') === 'true';

  const isSEOShown =
    company.showCategorySEOBlock &&
    (!company.hasDeepCatalog || (company.hasDeepCatalog && !isMobile));

  const isProductsShown =
    !company.hasDeepCatalog || !isMobile || showProductsParam;

  const {
    category,
    pageTitle,
    pageDescription,
    robots,
    canonical,
    mobileFilters,
    mobileSort,
    filters,
    filtersForMobile,
    hasActiveFilters,
    handleApplySorting,
    handleApplyMobileFilters,
    handleChangeFilter,
    handleResetFilters,
  } = useCategoryPage();

  const getProductFilters = (
    filters: FilterAPI[],
    options?: GetProductFilterOptions,
  ): ReactNode => {
    const { className: commonClassName, noCollapsible, mobile } = options || {};

    return filters.map(({ classes: classNames, ...filter }) => (
      <ProductFilterV3
        key={filter.code}
        onChange={(code, value) => handleChangeFilter(code, value, mobile)}
        filter={filter}
        selectedFilters={catalogStore.currentFilters}
        classes={{
          ...classNames,
          root: cn(commonClassName, classNames?.root),
        }}
        noCollapsible={noCollapsible}
        mobile={mobile}
      />
    ));
  };

  const breadcrumbs = (() => {
    const result: BreadcrumbItem[] = [
      { link: '/', label: t('home') },
      { link: '/shop', label: t('allCategories') },
    ];

    if (
      category.data?.depth === 3 &&
      category.data.parentSlug &&
      category.data.parentName
    ) {
      result.push({
        link: createCategoryUrl({
          id: category.data.parentSlug,
        }),
        label: category.data.parentName,
      });
    }

    if (category.data?.name) {
      result.push({ label: category.data.name });
    }

    return result;
  })();

  return (
    <div className={styles.root}>
      <Helmet>
        <title>
          {pageTitle}
          {company.config.name}
        </title>
        <meta name="description" content={pageDescription} />
        <meta data-react-helmet="true" name="robots" content={robots} />
        <link data-react-helmet="true" rel="canonical" href={canonical} />
      </Helmet>
      {category.isNotFound && <NotFound />}

      {!category.isNotFound && (
        <div className={styles.layout}>
          <div
            className={cn(styles.container, company.hasDeepCatalog && 'pt-2!')}
          >
            {isMobile && (
              <Toolbar
                isLoading={!category.data}
                mobileFilters={mobileFilters}
                mobileSort={mobileSort}
              />
            )}
            {isMobile &&
              !showProductsParam &&
              company.match({
                mcosmetic: (
                  <CategoriesList
                    category={category}
                    isLoading={!category.data}
                  />
                ),
                default: (
                  <Chips category={category} isLoading={!category.data} />
                ),
              })}

            {!isMobile && (
              <>
                <Breadcrumbs items={breadcrumbs} isLoading={!category.data} />
                <div className={styles.pageTitleWr}>
                  <h1 className={styles.pageTitle}>
                    {category.data?.name ?? (
                      <Skeleton containerClassName={styles.skeleton} />
                    )}
                  </h1>
                  <div className={styles.misc}>
                    {category.paginationRequest.isLoading || !category.data ? (
                      <Skeleton containerClassName={styles.counterSkeleton} />
                    ) : (
                      <span className={styles.counter}>
                        {t('phrases:itemsCount', {
                          count: category.paginationRequest.total,
                        })}
                      </span>
                    )}
                    <Sorting
                      value={`${catalogStore.currentSorting[0]}.${catalogStore.currentSorting[1]}`}
                      onChange={handleApplySorting}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={styles.body}>
              {!isMobile && (
                <Sidebar
                  hasActiveFilters={hasActiveFilters}
                  className={styles.sidebar}
                  beforeHeader={
                    <Chips
                      category={category}
                      wrap
                      className={styles.chips}
                      isLoading={!category.data}
                    />
                  }
                  handleResetFilters={handleResetFilters}
                  isLoading={!category.data}
                >
                  {!category.data ? (
                    <FiltersSkeleton />
                  ) : (
                    getProductFilters(
                      [
                        {
                          code: 'price',
                          name: 'Price',
                          showExpanded: true,
                          type: 'selector',
                          values: [],
                          classes: {
                            bodyContainer: styles.priceFilter,
                          },
                        },
                        ...filters,
                      ],
                      {
                        className: styles.sidebarFilter,
                      },
                    )
                  )}
                </Sidebar>
              )}
              {isProductsShown && (
                <ProductsView
                  category={category}
                  className={styles.productsGrid}
                  handleResetFilters={handleResetFilters}
                />
              )}
            </div>

            {isSEOShown && <CategorySEOBlock category={category} />}
          </div>
        </div>
      )}

      <MobileFilters
        open={mobileFilters.visible}
        onClose={mobileFilters.close}
        onApply={handleApplyMobileFilters}
        onReset={() => handleResetFilters({ mobile: true })}
        results={category.paginationRequest.total ?? 0}
        className={styles.mobileFiltersWr}
      >
        {getProductFilters(filtersForMobile, {
          className: styles.mobileFiltersRow,
          mobile: true,
        })}
      </MobileFilters>
      <MobileSorting
        open={mobileSort.visible}
        onClose={mobileSort.close}
        value={`${catalogStore.currentSorting[0]}.${catalogStore.currentSorting[1]}`}
        onChange={handleApplySorting}
      />
    </div>
  );
};

export default observer(CatalogV3);
